import { BUILDING_DEFINITIONS, SURFACE_PLOT_IDS } from "@kaeliux/shared";
import { calculateBuildingUpgradeCost, extractBuildingLevels } from "./buildings.js";
import { calculateStorageCapacity, calculateTotalProduction, calculatePopulationCapacity, } from "./resources.js";
import { DEFAULT_RESOURCE_RATES } from "./resourceEngine.js";
export function computePlanetDerivedStats(planet, mathematicsLevel = 0, architectureLevel = 0, teamPlanetOptions) {
    const economyMultiplier = teamPlanetOptions?.economyMultiplier ?? 1;
    const levels = extractBuildingLevels(planet.buildings);
    const production = calculateTotalProduction(levels, mathematicsLevel);
    const nextCosts = {};
    for (const type of Object.keys(BUILDING_DEFINITIONS)) {
        nextCosts[type] = calculateBuildingUpgradeCost(type, levels[type], architectureLevel);
    }
    return {
        rates: {
            metal: production.metal * economyMultiplier,
            minerals: production.minerals * economyMultiplier,
            gas: production.gas * economyMultiplier,
        },
        capacities: {
            metal: calculateStorageCapacity('metalStorage', levels.metalStorage) * economyMultiplier,
            minerals: calculateStorageCapacity('mineralStorage', levels.mineralStorage) * economyMultiplier,
            gas: calculateStorageCapacity('gasStorage', levels.gasStorage) * economyMultiplier,
        },
        populationCapacity: teamPlanetOptions?.populationCapacityOverride ?? calculatePopulationCapacity(levels.hangars),
        nextCosts,
    };
}
/**
 * Apply the cached derived stats to a planet doc so `resources.*.rate`,
 * `resources.*.capacity`, `population.capacity`, and every
 * `buildings[type].nextCost` are up to date.
 */
export function applyDerivedStats(planet, mathematicsLevel = 0, architectureLevel = 0, teamPlanetOptions) {
    const stats = computePlanetDerivedStats(planet, mathematicsLevel, architectureLevel, teamPlanetOptions);
    const buildings = { ...planet.buildings };
    for (const type of Object.keys(buildings)) {
        buildings[type] = {
            ...buildings[type],
            nextCost: stats.nextCosts[type],
        };
    }
    return {
        ...planet,
        buildings,
        resources: {
            ...planet.resources,
            metal: {
                ...planet.resources.metal,
                rate: stats.rates.metal || DEFAULT_RESOURCE_RATES.metal,
                capacity: stats.capacities.metal,
            },
            minerals: {
                ...planet.resources.minerals,
                rate: stats.rates.minerals || DEFAULT_RESOURCE_RATES.minerals,
                capacity: stats.capacities.minerals,
            },
            gas: {
                ...planet.resources.gas,
                rate: stats.rates.gas || DEFAULT_RESOURCE_RATES.gas,
                capacity: stats.capacities.gas,
            },
            population: {
                ...planet.resources.population,
                capacity: stats.populationCapacity,
            },
        },
    };
}
/**
 * Actual building levels plus one per pending (non-cancelled) buildingBuild
 * event — the level each building will reach once its queue drains. Queue
 * validation prices upgrades at these levels; prerequisites, by design, are
 * still checked against actual levels only.
 */
export function computeProjectedBuildingLevels(planet, pending) {
    const levels = extractBuildingLevels(planet.buildings);
    for (const event of pending) {
        if (event.eventType === "buildingBuild" && !event.cancelled) {
            levels[event.eventDetails.buildingType] += 1;
        }
    }
    return levels;
}
/**
 * Re-cache every `buildings[type].nextCost` at the building's *projected*
 * level so the client renders the price of the next queueable upgrade.
 * Rates and capacities are untouched — they follow actual levels only.
 */
export function withProjectedNextCosts(planet, pending, architectureLevel = 0) {
    const projected = computeProjectedBuildingLevels(planet, pending);
    const buildings = { ...planet.buildings };
    for (const type of Object.keys(buildings)) {
        buildings[type] = {
            ...buildings[type],
            nextCost: calculateBuildingUpgradeCost(type, projected[type], architectureLevel),
        };
    }
    return { ...planet, buildings };
}
/**
 * Assign a stable `plotId` (DS-28) to every built building (`level > 0`)
 * that predates plot tracking and has none yet. Pure and deterministic —
 * definition order within the zone (`BUILDING_DEFINITIONS`'s own iteration
 * order), each taking the lowest-index `SURFACE_PLOT_IDS` slot not already
 * claimed by an explicit assignment — so calling it twice on the same
 * planet always produces the same result, and it never collides with a
 * plot a player actually chose.
 *
 * Called both server-side (so the assignment persists on the next write —
 * see `runAdvance`) and client-side purely for rendering a planet that
 * hasn't been written back yet, so a legacy building never appears
 * plotless even before that write happens.
 */
export function backfillPlanetPlotIds(planet) {
    const claimed = new Set();
    for (const type of Object.keys(planet.buildings)) {
        const plotId = planet.buildings[type]?.plotId;
        if (plotId)
            claimed.add(plotId);
    }
    const needsBackfill = Object.keys(planet.buildings).some((type) => (planet.buildings[type]?.level ?? 0) > 0 && !planet.buildings[type]?.plotId);
    if (!needsBackfill)
        return planet;
    const buildings = { ...planet.buildings };
    for (const type of Object.keys(BUILDING_DEFINITIONS)) {
        const building = buildings[type];
        if (!building || building.level <= 0 || building.plotId)
            continue;
        const zone = BUILDING_DEFINITIONS[type].zone;
        const freePlot = SURFACE_PLOT_IDS[zone].find((id) => !claimed.has(id));
        if (!freePlot)
            continue; // Cannot happen while plots.test.ts holds, but never throw over a display concern.
        claimed.add(freePlot);
        buildings[type] = { ...building, plotId: freePlot };
    }
    return { ...planet, buildings };
}
export function initialPlanetBuildings() {
    const result = {};
    for (const type of Object.keys(BUILDING_DEFINITIONS)) {
        result[type] = {
            type,
            level: 0,
            nextCost: calculateBuildingUpgradeCost(type, 0),
        };
    }
    return result;
}
//# sourceMappingURL=derivedStats.js.map